import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLeads } from '../../../context/LeadContext';
import { useAuth } from '../../../context/AuthContext';
import leadActivityService from '../../../api/crm/leads/leadActivityService';
import { API_ENDPOINTS } from '../../../api/config';
import configService from '../../../api/crm/config/configService';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Save,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  Clock,
  User,
  Building,
  ChevronDown,
  Search
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  sanitizeTextInput,
  validateNumberInput,
  handleTextInputChange,
  handleNumberInputChange,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { USER_ROLES } from '../../../utils/constants';

const LogActivity = ({ leadId: propLeadId, activityId: propActivityId, onBack, onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: paramLeadId } = useParams();
  const [searchParams] = useSearchParams();
  const editModeParam = searchParams.get('edit') === 'true';
  const activityIdParam = searchParams.get('activityId');

  // Prioritize props over URL params
  const id = propLeadId || paramLeadId;
  const activityId = propActivityId || activityIdParam;
  const isEditMode = !!propActivityId || editModeParam;
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const currentUserId =
    user?.id ||
    user?.userId ||
    user?._id ||
    (() => {
      try {
        const persisted = JSON.parse(localStorage.getItem('user_data') || '{}');
        return persisted?.id || persisted?.userId || persisted?._id;
      } catch (error) {
        return undefined;
      }
    })();
  const { getLeadById, updateLead, leads } = useLeads();

  const { data: activityStatuses = [] } = useQuery({
    queryKey: ['activityStatuses'],
    queryFn: async () => {
      const response = await configService.activityStatusService.getDropdown();
      if (response.success) {
        return response.data || [];
      }
      return [];
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [leadData, setLeadData] = useState(null);
  const [isLoadingLead, setIsLoadingLead] = useState(true);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [filteredAssigneeOptions, setFilteredAssigneeOptions] = useState([]);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);

  const formatDurationForInput = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return '';
    return Number(numberValue.toFixed(2)).toString();
  };

  const formatDateTimeLocal = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getCurrentLocalDateTimeString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getMinFollowUpDateTimeString = () => {
    const minTime = new Date(Date.now() + 60 * 60 * 1000);
    const year = minTime.getFullYear();
    const month = String(minTime.getMonth() + 1).padStart(2, '0');
    const day = String(minTime.getDate()).padStart(2, '0');
    const hours = String(minTime.getHours()).padStart(2, '0');
    const minutes = String(minTime.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getUserDisplayName = (person) => {
    if (!person) return 'Unknown User';
    if (typeof person === 'string') return person;
    if (person.name) return person.name;
    if (person.firstName && person.lastName) {
      return `${person.firstName} ${person.lastName}`;
    }
    if (person.firstName) return person.firstName;
    if (person.lastName) return person.lastName;
    if (person.email) return person.email;
    return 'Unknown User';
  };

  const normalizeAssignee = (person) => ({
    id: person?.user || person?._id || person?.id || '',
    name: getUserDisplayName(person),
    email: person?.email || '',
    role: person?.role || '',
    designation: person?.designation || person?.position || ''
  });

  const loadAssignees = useCallback(async (search = '') => {
    if (isSalesExecutive) {
      const selfAssignee = normalizeAssignee({
        id: currentUserId,
        name: getUserDisplayName(user),
        email: user?.email || '',
        role: user?.role || USER_ROLES.SALES_EXECUTIVE,
        designation: user?.designation || 'Sales Executive'
      });

      setAssigneeOptions([selfAssignee]);
      setFilteredAssigneeOptions([selfAssignee]);
      setSelectedAssignee(selfAssignee);
      return [selfAssignee];
    }

    if (!user) {
      return [];
    }

    setLoadingAssignees(true);
    try {
      const trimmedSearch = (search || '').trim();
      const params = new URLSearchParams();
      if (trimmedSearch) {
        params.append('search', trimmedSearch);
      }
      params.append('limit', '50');
      params.append('roleFilter', 'sales');

      if (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER) {
        params.append('managerId', user._id || user.id || currentUserId);
      }

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];
        let transformedUsers = crmUsers
          .filter((crmUser) => {
            const role = (crmUser.role || '').toLowerCase().replace(/\s+/g, '_');
            return [
              'sales_manager',
              'sales_executive',
              'business_relationship_manager',
              'business_development_executive',
              'export_manager',
              'export_executive'
            ].includes(role);
          })
          .map((crmUser) => normalizeAssignee(crmUser));

        // Include the logged-in manager if they are sales-manager or export-manager
        const isManager = user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER;
        if (isManager && currentUserId) {
          const managerName = getUserDisplayName(user) || '';
          const managerEmail = user?.email || '';
          const searchTermLower = (search || '').toLowerCase().trim();
          const matchesSearch = !searchTermLower ||
            managerName.toLowerCase().includes(searchTermLower) ||
            managerEmail.toLowerCase().includes(searchTermLower);

          if (matchesSearch) {
            const managerExists = transformedUsers.some(u => String(u.id) === String(currentUserId));
            if (!managerExists) {
              const selfAssignee = normalizeAssignee({
                ...user,
                _id: currentUserId,
                user: currentUserId
              });
              selfAssignee.name = `${selfAssignee.name} (Me)`;
              transformedUsers.push(selfAssignee);
            }
          }
        }

        transformedUsers.sort((a, b) => a.name.localeCompare(b.name));

        setAssigneeOptions(transformedUsers);
        setFilteredAssigneeOptions(transformedUsers);
        return transformedUsers;
      }

      setAssigneeOptions([]);
      setFilteredAssigneeOptions([]);
      return [];
    } catch (error) {
      setAssigneeOptions([]);
      setFilteredAssigneeOptions([]);
      return [];
    } finally {
      setLoadingAssignees(false);
    }
  }, [currentUserId, isSalesExecutive, user]);

  // Fetch activity data if in edit mode
  useEffect(() => {
    const fetchActivityData = async () => {
      if (isEditMode && activityId) {
        setIsLoadingActivity(true);
        try {
          const result = await leadActivityService.getActivityById(activityId);
          if (result.success && result.activity) {
            setActivityData(result.activity);
            if (result.activity.performedBy) {
              const performer = typeof result.activity.performedBy === 'object'
                ? normalizeAssignee(result.activity.performedBy)
                : { id: result.activity.performedBy, name: 'Selected Assignee', email: '', role: '', designation: '' };
              setSelectedAssignee(performer);
            }

            // Pre-fill form with activity data
            // For calls, get duration from callMetadata.talkDuration (in seconds) and convert to minutes
            const activity = result.activity;
            let durationValue = '';
            if (activity.type === 'call' && activity.callMetadata?.talkDuration) {
              // Convert seconds to minutes for display
              durationValue = formatDurationForInput(activity.callMetadata.talkDuration / 60);
            } else if (activity.duration) {
              durationValue = formatDurationForInput(activity.duration);
            }

            setFormData({
              type: activity.type || 'call',
              title: activity.title || '',
              description: activity.description || '',
              duration: durationValue,
              scheduledFor: activity.scheduledFor
                ? formatDateTimeLocal(activity.scheduledFor)
                : (activity.performedAt ? formatDateTimeLocal(activity.performedAt) : getCurrentLocalDateTimeString()),
              outcome: activity.outcome || 'scheduled',
              activityStatus: activity.activityStatus?._id || activity.activityStatus?.id || activity.activityStatus || '',
              activityNature: activity.activityNature || 'past'
            });
          } else {
            toast.error(result.error || 'Failed to load activity data');
            if (onBack) {
              onBack();
            } else {
              navigate(-1);
            }
          }
        } catch (error) {
          toast.error('An error occurred while loading activity data');
          if (onBack) {
            onBack();
          } else {
            navigate(-1);
          }
        } finally {
          setIsLoadingActivity(false);
        }
      }
    };

    fetchActivityData();
  }, [isEditMode, activityId, navigate]);

  // Fetch lead data on component mount
  useEffect(() => {
    const fetchLeadData = async () => {
      setIsLoadingLead(true);
      try {
        // In edit mode, use lead from activity data if available
        if (isEditMode && activityData && activityData.lead) {
          const lead = activityData.lead;
          setLeadData({
            id: lead._id || lead.id,
            _id: lead._id || lead.id,
            firstName: lead.firstName,
            lastName: lead.lastName,
            email: lead.email,
            company: lead.company,
            phone: lead.phone
          });
          setIsLoadingLead(false);
          return;
        }

        // First, try to find the lead in the local leads array
        // Normalize IDs for comparison (convert both to strings)
        const normalizedId = String(id).trim();
        const localLead = leads.find(lead => {
          const leadId = String(lead.id || lead._id || lead.leadId || '').trim();
          return leadId === normalizedId;
        });

        if (localLead) {
          setLeadData(localLead);
          setIsLoadingLead(false);
        } else {
          // If not found locally, fetch from API
          const fetchedLead = await getLeadById(id);
          if (fetchedLead) {
            setLeadData(fetchedLead);
          } else {
            setLeadData(null);
          }
          setIsLoadingLead(false);
        }
      } catch (error) {
        setLeadData(null);
        setIsLoadingLead(false);
      }
    };

    if (id) {
      fetchLeadData();
    } else {
      setIsLoadingLead(false);
      setLeadData(null);
    }
  }, [id, getLeadById, leads, isEditMode, activityData]);

  useEffect(() => {
    loadAssignees(assigneeSearchTerm);
  }, [loadAssignees]);

  useEffect(() => {
    if (!openAssigneeDropdown) return;

    const handler = setTimeout(() => {
      loadAssignees(assigneeSearchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [assigneeSearchTerm, openAssigneeDropdown, loadAssignees]);

  useEffect(() => {
    if (openAssigneeDropdown && assigneeDropdownRef.current) {
      const rect = assigneeDropdownRef.current.getBoundingClientRect();
      setAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openAssigneeDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(event.target) &&
        !event.target.closest('[data-dropdown-assignee]')
      ) {
        setOpenAssigneeDropdown(false);
      }
    };

    if (openAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }

    return undefined;
  }, [openAssigneeDropdown]);

  useEffect(() => {
    if (isSalesExecutive && currentUserId) {
      setSelectedAssignee(normalizeAssignee({
        id: currentUserId,
        name: getUserDisplayName(user),
        email: user?.email || '',
        role: user?.role || USER_ROLES.SALES_EXECUTIVE,
        designation: user?.designation || 'Sales Executive'
      }));
    }
  }, [currentUserId, isSalesExecutive, user]);

  useEffect(() => {
    if (!activityData?.performedBy || isSalesExecutive) return;

    const performerId = activityData.performedBy?._id || activityData.performedBy?.id || activityData.performedBy;
    const matchedAssignee = assigneeOptions.find(option => String(option.id) === String(performerId));

    if (matchedAssignee) {
      setSelectedAssignee(matchedAssignee);
    }
  }, [activityData, assigneeOptions, isSalesExecutive]);

  const [formData, setFormData] = useState({
    activityNature: 'past',
    type: 'call',
    title: '',
    description: '',
    duration: '',
    scheduledFor: getCurrentLocalDateTimeString(),
    outcome: '',
    activityStatus: ''
  });

  // When "Current Activity" is selected, keep scheduledFor synced to now
  useEffect(() => {
    if (formData.activityNature !== 'past') return;

    // Set immediately
    setFormData(prev => ({ ...prev, scheduledFor: getCurrentLocalDateTimeString() }));

    // Refresh every minute so the value stays current
    const interval = setInterval(() => {
      setFormData(prev => {
        if (prev.activityNature !== 'past') return prev;
        return { ...prev, scheduledFor: getCurrentLocalDateTimeString() };
      });
    }, 60000);

    return () => clearInterval(interval);
  }, [formData.activityNature]);

  // Activity types
  const activityTypes = [
    { value: 'call', label: 'Phone Call', icon: Phone },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'meeting', label: 'Meeting', icon: Calendar },
    { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { value: 'note', label: 'Note', icon: FileText }
  ];


  // Helper function to get lead name
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else {
      return 'Unknown Lead';
    }
  };

  const handleInputChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
    let processedValue = value;
    let error = '';

    if (fieldType === 'text') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'number') {
      const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
      const minValue = NUMBER_LIMITS[field]?.min || 0;
      const numValidation = validateNumberInput(value, maxValue, minValue);
      processedValue = numValidation.value;
      error = numValidation.error || '';
    } else if (fieldType === 'decimal') {
      if (value === '' || value === null || value === undefined) {
        processedValue = '';
      } else {
        let strVal = String(value).replace(/[^0-9.]/g, '');
        const parts = strVal.split('.');
        if (parts.length > 2) {
          strVal = parts[0] + '.' + parts.slice(1).join('');
        }
        const updatedParts = strVal.split('.');
        if (updatedParts[1] !== undefined) {
          strVal = updatedParts[0] + '.' + updatedParts[1].slice(0, 2);
        }
        const maxVal = NUMBER_LIMITS[field]?.max || (typeof maxLength === 'number' ? maxLength : 999);
        const numVal = parseFloat(strVal);
        if (!isNaN(numVal) && numVal > maxVal) {
          error = `Duration must be ${maxVal} or less`;
        }
        processedValue = strVal;
      }
    } else if (fieldType === 'textarea') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'datetime-local') {
      // For datetime-local inputs, use the value as-is without sanitization
      processedValue = value || '';
    }

    // Date/Time validation for activityNature vs scheduledFor
    let dateError = '';
    const targetNature = field === 'activityNature' ? processedValue : formData.activityNature;
    const targetDateStr = field === 'scheduledFor' ? processedValue : formData.scheduledFor;

    if (targetDateStr) {
      const selectedDate = new Date(targetDateStr);
      const now = new Date();
      const minFollowUpTime = new Date(now.getTime() + 60 * 60 * 1000);
      const gracePeriodFuture = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      if (targetNature === 'followup' && selectedDate < minFollowUpTime) {
        dateError = 'Follow up date & time must be at least 1 hour in the future';
      } else if (targetNature === 'past' && selectedDate > gracePeriodFuture) {
        dateError = 'Activity date & time cannot be in the future';
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Set or clear error
    const activeError = field === 'scheduledFor' ? dateError : error;
    setErrors(prev => {
      const newErrors = { ...prev };
      if (activeError) {
        newErrors[field] = activeError;
      } else {
        delete newErrors[field];
      }

      if (field === 'activityNature') {
        if (dateError) {
          newErrors.scheduledFor = dateError;
        } else {
          delete newErrors.scheduledFor;
        }
      }

      return newErrors;
    });
  };

  const handleBlur = (field) => {
    let error = '';
    const value = formData[field];

    if (field === 'title') {
      if (!value || value.trim() === '') {
        error = 'Activity title is required';
      }
    } else if (field === 'description') {
      if (!value || value.trim() === '') {
        error = 'Activity description is required';
      }
    } else if (field === 'scheduledFor') {
      if (value) {
        const selectedDate = new Date(value);
        const now = new Date();
        const minFollowUpTime = new Date(now.getTime() + 60 * 60 * 1000);
        const gracePeriodFuture = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        if (formData.activityNature === 'followup' && selectedDate < minFollowUpTime) {
          error = 'Follow up date & time must be at least 1 hour in the future. Please update the date and time.';
        } else if (formData.activityNature === 'past' && selectedDate > gracePeriodFuture) {
          error = 'Activity date & time cannot be in the future for Past Activity. Please update the date and time or change Activity Nature to "Follow Up".';
        }
      }
    }

    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
      if (field === 'scheduledFor') {
        toast.error(error);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title || formData.title.trim() === '') {
      newErrors.title = 'Activity title is required';
    }

    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Activity description is required';
    }

    // Duration validation - optional, but if provided must be a positive number
    if (formData.type === 'call') {
      const durationValue = formData.duration;
      if (
        durationValue !== undefined &&
        durationValue !== null &&
        durationValue !== ''
      ) {
        const numVal = parseFloat(durationValue);
        if (isNaN(numVal) || numVal <= 0) {
          newErrors.duration = 'Duration must be a positive number';
        } else if (numVal > NUMBER_LIMITS.duration.max) {
          newErrors.duration = `Duration must be ${NUMBER_LIMITS.duration.max} or less`;
        }
      }
    }

    if (formData.scheduledFor) {
      const selectedDate = new Date(formData.scheduledFor);
      const now = new Date();
      const minFollowUpTime = new Date(now.getTime() + 60 * 60 * 1000);
      const gracePeriodFuture = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      if (formData.activityNature === 'followup' && selectedDate < minFollowUpTime) {
        const errorMsg = 'Follow up date & time must be at least 1 hour in the future. Please update the date and time.';
        newErrors.scheduledFor = errorMsg;
        toast.error(errorMsg);
      } else if (formData.activityNature === 'past' && selectedDate > gracePeriodFuture) {
        const errorMsg = 'Activity date & time cannot be in the future for Past Activity. Please update the date and time or change Activity Nature to "Follow Up".';
        newErrors.scheduledFor = errorMsg;
        toast.error(errorMsg);
      }
    }

    if (!isSalesExecutive && !selectedAssignee?.id) {
      newErrors.performedBy = 'Assignee is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare activity data for API
      // Convert datetime-local value to ISO string for scheduledFor
      let scheduledForISO = undefined;
      if (formData.scheduledFor) {
        // datetime-local format is "YYYY-MM-DDTHH:mm"
        // Convert to ISO string by creating a Date object
        const localDateTime = new Date(formData.scheduledFor);
        if (!isNaN(localDateTime.getTime())) {
          scheduledForISO = localDateTime.toISOString();
        } else {
          // If parsing fails, use the value as-is (might already be in ISO format)
          scheduledForISO = formData.scheduledFor;
        }
      }

      let effectiveOutcome = formData.outcome || undefined;
      if (formData.activityNature === 'past' && !effectiveOutcome && !formData.activityStatus) {
        effectiveOutcome = 'completed';
      }

      const activityPayload = {
        type: formData.type,
        title: formData.title.trim(),
        description: formData.description.trim(),
        performedBy: selectedAssignee?.id || currentUserId,
        duration: formData.type === 'call' && formData.duration && !isNaN(parseFloat(formData.duration)) ? parseFloat(formData.duration) : undefined,
        // For calls, store duration in callMetadata.talkDuration for consistency with automated calls
        callMetadata: formData.type === 'call' && formData.duration && !isNaN(parseFloat(formData.duration)) ? {
          talkDuration: parseFloat(formData.duration) * 60 // Convert minutes to seconds
        } : undefined,
        scheduledFor: scheduledForISO,
        performedAt: scheduledForISO || (activityData?.performedAt ? new Date(activityData.performedAt).toISOString() : new Date().toISOString()),
        outcome: effectiveOutcome,
        activityStatus: formData.activityStatus || undefined,
        activityNature: formData.activityNature
      };

      // Prepare activity data for API

      let result;
      if (isEditMode && activityId) {
        // Update existing activity
        result = await leadActivityService.updateActivity(activityId, activityPayload);
        if (result.success) {
          toast.success('Activity updated successfully!');
          if (onSuccess) {
            onSuccess();
          } else {
            navigate(-1);
          }
        } else {
          toast.error(result.error || 'Failed to update activity. Please try again.');
        }
      } else {
        // Create new activity
        activityPayload.performedAt = new Date().toISOString();
        result = await leadActivityService.createActivity(id, activityPayload);
        if (result.success) {
          toast.success('Activity logged successfully!');
          if (onSuccess) {
            onSuccess();
          } else {
            navigate(-1);
          }
        } else {
          toast.error(result.error || 'Failed to log activity. Please try again.');
        }
      }

    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Always go back to lead activities page OR call onBack if in drawer
    // if (onBack) {
    //   onBack();
    // } else {
    navigate(-1);
    // }
  };

  // Show loading state while fetching lead or activity data
  if (isLoadingLead || isLoadingActivity) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lead information...</p>
        </div>
      </div>
    );
  }

  // Show error state if lead not found
  if (!leadData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Lead not found</p>
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back to Leads
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={handleCancel}
        >
          Back
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Activity' : 'Log Activity'}
          </h1>
          <p className="text-gray-600 break-words">
            {isEditMode ? 'Edit activity details' : `Log an activity for ${getLeadName(leadData)}`}
          </p>
        </div>
      </div>

      {/* Lead Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3 min-w-0">
            <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-words">{getLeadName(leadData)}</p>
              <p className="text-xs text-gray-500">Contact Person</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-words">{leadData.company || 'No company'}</p>
              <p className="text-xs text-gray-500">Company</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <Mail className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-all break-words">{leadData.email || 'No email'}</p>
              <p className="text-xs text-gray-500">Email</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <Phone className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-words">{leadData.phone || 'No phone'}</p>
              <p className="text-xs text-gray-500">Phone</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity Details</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Activity Nature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activity Nature <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex space-x-4">
              <label
                className={`flex-1 flex items-center justify-center px-4 py-2.5 border rounded-lg cursor-pointer transition-colors text-sm font-medium ${formData.activityNature === 'past'
                  ? 'border-agrosaf-green bg-agrosaf-green/10 text-agrosaf-green'
                  : 'border-gray-300 hover:border-gray-400 text-gray-700'
                  }`}
              >
                <input
                  type="radio"
                  name="activityNature"
                  value="past"
                  checked={formData.activityNature === 'past'}
                  onChange={() => handleInputChange('activityNature', 'past')}
                  className="sr-only"
                />
                Current Activity
              </label>
              <label
                className={`flex-1 flex items-center justify-center px-4 py-2.5 border rounded-lg cursor-pointer transition-colors text-sm font-medium ${formData.activityNature === 'followup'
                  ? 'border-agrosaf-green bg-agrosaf-green/10 text-agrosaf-green'
                  : 'border-gray-300 hover:border-gray-400 text-gray-700'
                  }`}
              >
                <input
                  type="radio"
                  name="activityNature"
                  value="followup"
                  checked={formData.activityNature === 'followup'}
                  onChange={() => handleInputChange('activityNature', 'followup')}
                  className="sr-only"
                />
                Follow Up
              </label>
            </div>
          </div>

          {/* Assignee */}
          {!isSalesExecutive && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assignee <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative" ref={assigneeDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    const isOpening = !openAssigneeDropdown;
                    setOpenAssigneeDropdown(isOpening);
                    if (isOpening && assigneeOptions.length === 0) {
                      loadAssignees(assigneeSearchTerm);
                    }
                  }}
                  disabled={loadingAssignees}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <span className="truncate text-sm whitespace-nowrap">
                    {selectedAssignee?.name || 'Select Assignee'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
                </button>

                {openAssigneeDropdown && (
                  <div
                    data-dropdown-assignee
                    className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                    style={{
                      top: `${assigneeDropdownPosition.top}px`,
                      left: `${assigneeDropdownPosition.left}px`,
                      minWidth: '240px',
                      maxWidth: '350px',
                      width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                    }}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={assigneeSearchTerm}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setAssigneeSearchTerm(value);
                          }}
                          placeholder="Search assignees..."
                          maxLength={100}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                      {loadingAssignees && filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          Loading assignees...
                        </div>
                      ) : filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                          {assigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          {filteredAssigneeOptions.map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                setSelectedAssignee(option);
                                setOpenAssigneeDropdown(false);
                                setAssigneeSearchTerm('');
                              }}
                              className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${selectedAssignee?.id === option.id
                                ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium'
                                : 'hover:bg-gray-50 text-gray-700'
                                }`}
                            >
                              <div className="truncate font-medium break-words">{option.name}</div>
                              {option.email && (
                                <div className="text-xs text-gray-500 truncate break-words">{option.email}</div>
                              )}
                              {option.role && (
                                <div className="text-xs text-gray-500 truncate capitalize break-words">{option.role.replace(/_/g, ' ')}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.performedBy && <p className="text-red-500 text-sm mt-1">{errors.performedBy}</p>}
            </div>
          )}

          {/* Activity Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activity Type <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {activityTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleInputChange('type', type.value)}
                    className={`p-3 border rounded-lg text-center transition-colors ${formData.type === type.value
                      ? 'border-agrosaf-green bg-agrosaf-green/10 text-agrosaf-green'
                      : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <IconComponent className="h-5 w-5 mx-auto mb-2" />
                    <span className="text-xs font-medium">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activity Title <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value, 'text', 200)}
              onBlur={() => handleBlur('title')}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.title ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
              placeholder="Enter activity title"
              maxLength={200}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
              <span className="text-xs text-gray-500 ml-auto">
                {getCharacterCount(formData.title, 200)}
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value, 'textarea', FIELD_LIMITS.notes)}
              onBlur={() => handleBlur('description')}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.description ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
              placeholder="Describe what happened during this activity"
              maxLength={FIELD_LIMITS.notes}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
              <span className="text-xs text-gray-500 ml-auto">
                {getCharacterCount(formData.description, FIELD_LIMITS.notes)}
              </span>
            </div>
          </div>

          {/* Duration (for calls) */}
          {formData.type === 'call' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (minutes)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={NUMBER_LIMITS.duration.max}
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', e.target.value, 'decimal', NUMBER_LIMITS.duration.max)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.duration ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="Enter duration in minutes (e.g. 23.12)"
              />
              {errors.duration && <p className="text-red-500 text-sm mt-1">{errors.duration}</p>}
            </div>
          )}

          {/* Scheduled For */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {formData.activityNature === 'followup' ? 'Follow Up Date & Time' : 'Activity Date & Time'}
            </label>
            <input
              type="datetime-local"
              value={formData.scheduledFor}
              min={formData.activityNature === 'followup' ? getMinFollowUpDateTimeString() : undefined}
              onChange={(e) => handleInputChange('scheduledFor', e.target.value, 'datetime-local')}
              onBlur={() => handleBlur('scheduledFor')}
              onClick={(e) => e.target.showPicker?.()}
              disabled={formData.activityNature === 'past'}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formData.activityNature === 'past'
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                  : errors.scheduledFor
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300'
                }`}
            />
            {formData.activityNature === 'past' && (
              <p className="text-xs text-gray-400 mt-1">Current date &amp; time will be used automatically.</p>
            )}
            {errors.scheduledFor && formData.activityNature !== 'past' && <p className="text-red-500 text-sm mt-1">{errors.scheduledFor}</p>}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={formData.activityStatus}
              onChange={(e) => handleInputChange('activityStatus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent capitalize"
            >
              <option value="">Select Status</option>
              {activityStatuses.map((status) => (
                <option key={status._id || status.id} value={status._id || status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <PermissionGate permission="lead_management" action={isEditMode ? "update" : "create"}>
              <Button
                type="submit"
                variant="primary"
                icon={<Save className="h-4 w-4" />}
                disabled={isSubmitting}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                {isSubmitting
                  ? (isEditMode ? 'Updating Activity...' : 'Logging Activity...')
                  : (isEditMode ? 'Update Activity' : 'Log Activity')}
              </Button>
            </PermissionGate>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogActivity;
